// Library calls (/library). Every change refetches the whole library, which is one request.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { importToLibrary } from '@/features/plan/importFiles';
import type { LibraryContents, LibraryFolder, Selection } from './model';

const libraryKey = ['library'] as const;

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await apiFetch(path, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${method} ${path}: ${response.status}`);
  return (response.status === 204 ? undefined : await response.json()) as T;
}

export function useLibrary() {
  return useQuery({ queryKey: libraryKey, queryFn: () => send<LibraryContents>('GET', '/library') });
}

function useLibraryMutation<T, R = unknown>(run: (input: T) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => queryClient.invalidateQueries({ queryKey: libraryKey }),
  });
}

export const useCreateFolder = () =>
  useLibraryMutation((input: { name: string; parentGuid: string | null }) =>
    send<LibraryFolder>('POST', '/library/folders', input),
  );

export const useUpdateFolder = () =>
  useLibraryMutation((input: { guid: string; name: string; parentGuid: string | null }) =>
    send<LibraryFolder>('PUT', `/library/folders/${input.guid}`, {
      name: input.name,
      parentGuid: input.parentGuid,
    }),
  );

export const useMove = () =>
  useLibraryMutation((input: Selection & { fromFolderGuid: string | null; toFolderGuid: string | null }) =>
    send('POST', '/library/move', input),
  );

export const useCopy = () =>
  useLibraryMutation((input: Selection & { toFolderGuid: string | null }) =>
    send('POST', '/library/copy', input),
  );

export const useDelete = () =>
  useLibraryMutation((input: Selection) => send('POST', '/library/delete', input));

/** Imports GPX/TCX/zip files into a folder (null = top level). */
export const useImport = () =>
  useLibraryMutation((input: { files: File[]; folderGuid: string | null }) =>
    importToLibrary(input.files, input.folderGuid),
  );
